// Páginas que o colaborador abre pelo link do WhatsApp para assinar o vale
// ou o acerto no celular dele. Não precisa de conta nem de login: o link
// carrega um código que só serve para aquele vale (ou acerto), uma vez, por
// 30 dias.
import { assinaturaSuficiente, caminhoDosTracos, quadroDeAssinatura } from '../lib/assinatura.js';
import { NOMES_CREDITO, NOMES_DESCONTO } from '../lib/demonstrativo.js';
import { h, icone, trocar } from '../lib/dom.js';
import { ehErroDeRede } from '../local/sincronia.js';
import { mensagemDeErro } from '../lib/erros.js';
import { reaisPorExtenso } from '../lib/extenso.js';
import { data, dinheiro, placa as formatarPlaca } from '../lib/formato.js';
import { supabase } from '../supabase.js';
import { carregando, faixa } from '../ui/componentes.js';

const FORMAS = { pix: 'Pix', dinheiro: 'Dinheiro', transferencia: 'Transferência', cartao: 'Cartão', boleto: 'Boleto', outro: 'Outro' };

// Data e hora no fuso do próprio celular de quem assina.
function quando(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)} às ${
    new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(d)}`;
}

export function telaAssinar({ params }) {
  const codigo = params.codigo;
  const corpo = h('div', { class: 'secao' }, carregando('Abrindo o vale…'));

  const tentarDeNovo = () => h('button', { class: 'botao cheio', type: 'button', onClick: carregar }, 'Tentar de novo');

  async function carregar() {
    trocar(corpo, carregando('Abrindo o vale…'));
    const { data: vale, error } = await supabase.rpc('vale_para_assinar', { p_token: codigo });
    if (error) {
      trocar(corpo,
        faixa(ehErroDeRede(error) ? 'Sem sinal agora. Quando a internet voltar, tente de novo.' : mensagemDeErro(error), 'alerta', 'Não deu para abrir'),
        tentarDeNovo());
      return;
    }
    if (vale.situacao === 'invalido') {
      trocar(corpo, faixa('Este link não vale mais: o vale pode ter sido corrigido ou excluído. Peça um novo a quem mandou.', 'perigo', 'Link inválido'));
      return;
    }
    if (vale.situacao === 'vencido') {
      trocar(corpo, faixa('O link vale 30 dias e este já passou. Peça um novo a quem mandou.', 'alerta', 'Link vencido'));
      return;
    }
    if (vale.situacao === 'assinado') {
      trocar(corpo, faixa(`Este vale já foi assinado em ${quando(vale.assinado_em)}. Obrigado!`, 'ok', 'Já assinado'));
      return;
    }
    mostrarParaAssinar(vale);
  }

  function mostrarParaAssinar(vale) {
    const quadro = quadroDeAssinatura();
    const erro = h('div', { role: 'alert' });
    const botao = h('button', { class: 'botao cheio', type: 'button', disabled: true }, 'Confirmar assinatura');
    quadro.aoMudar(() => {
      botao.disabled = !assinaturaSuficiente(quadro.tracos());
    });
    botao.addEventListener('click', async () => {
      const tracos = quadro.tracos();
      if (!assinaturaSuficiente(tracos)) return;
      botao.disabled = true;
      botao.textContent = 'Enviando…';
      trocar(erro);
      const { data: r, error } = await supabase.rpc('assinar_vale', { p_token: codigo, p_assinatura: caminhoDosTracos(tracos) });
      if (error) {
        trocar(erro, faixa(ehErroDeRede(error) ? 'Sem sinal agora. A assinatura continua no quadro: tente de novo quando a internet voltar.' : mensagemDeErro(error), 'perigo'));
        botao.disabled = false;
        botao.textContent = 'Confirmar assinatura';
        return;
      }
      quadro.desligar();
      trocar(corpo,
        faixa(`Assinatura registrada em ${quando(r?.assinado_em)}. Obrigado!`, 'ok', 'Pronto'),
        h('p', { class: 'pequeno' }, 'Pode fechar esta página.'));
    });

    const viagem = vale.placa && vale.viagem_saida ? `Caminhão ${formatarPlaca(vale.placa)}, saída em ${data(vale.viagem_saida)}` : null;
    trocar(corpo,
      h('h1', null, vale.tipo === 'pagamento' ? 'Pagamento para assinar' : 'Vale para assinar'),
      h('p', null, `${vale.empresa} registrou ${vale.tipo === 'pagamento' ? 'um pagamento' : 'um vale'} para ${vale.colaborador ?? 'você'}:`),
      h('div', { class: 'cartao destaque' }, h('div', { class: 'cartao-corpo' },
        h('span', { class: 'valor-grande' }, dinheiro(vale.valor)),
        h('span', { class: 'cartao-linha' }, reaisPorExtenso(vale.valor)))),
      h('dl', { class: 'linha-dados' },
        h('dt', null, 'Data'), h('dd', null, data(vale.data)),
        vale.forma && [h('dt', null, 'Como foi pago'), h('dd', null, FORMAS[vale.forma] ?? vale.forma)],
        viagem && [h('dt', null, 'Viagem'), h('dd', null, viagem)],
        vale.descricao && [h('dt', null, 'Observação'), h('dd', null, vale.descricao)]),
      h('p', null, h('strong', null, 'Assine com o dedo no quadro abaixo.'), ' Ao assinar, você confirma que recebeu este valor.'),
      quadro.elemento,
      erro,
      h('div', { class: 'acoes' },
        botao,
        h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => quadro.limpar() }, 'Limpar e assinar de novo')));
  }

  carregar().catch((e) => {
    console.error(e);
    trocar(corpo, faixa(mensagemDeErro(e), 'perigo'), tentarDeNovo());
  });

  return h('div', { class: 'pagina sem-abas' },
    h('header', { class: 'topo' }, h('div', { class: 'topo-linha' }, h('h1', null, 'HSA Frota'))),
    h('main', { class: 'conteudo assinar', id: 'conteudo', tabindex: '-1' },
      h('div', { class: 'marca-pequena' }, icone('caminhao', { tamanho: 20 }), ' Assinatura de recibo'),
      corpo));
}

// ---------------------------------------------------------------------
// Acerto: o colaborador confere o demonstrativo e assina no celular dele.
// ---------------------------------------------------------------------
const NOMES_TIPO = { credito: 'Créditos (o que você ganhou)', vale: 'Vales (você já recebeu)', despesa: 'Despesas pagas por você', desconto: 'Descontos' };

function textoDoLancamento(l) {
  if (l.descricao) return l.descricao;
  if (l.tipo === 'credito') return NOMES_CREDITO[l.subtipo] ?? 'Crédito';
  if (l.tipo === 'desconto') return NOMES_DESCONTO[l.subtipo] ?? 'Desconto';
  if (l.tipo === 'vale') return `Vale${l.forma && FORMAS[l.forma] ? ` (${FORMAS[l.forma]})` : ''}`;
  return 'Despesa';
}

export function telaAssinarAcerto({ params }) {
  const codigo = params.codigo;
  const corpo = h('div', { class: 'secao' }, carregando('Abrindo o acerto…'));
  const tentarDeNovo = () => h('button', { class: 'botao cheio', type: 'button', onClick: carregar }, 'Tentar de novo');

  async function carregar() {
    trocar(corpo, carregando('Abrindo o acerto…'));
    const { data: acerto, error } = await supabase.rpc('acerto_para_assinar', { p_token: codigo });
    if (error) {
      trocar(corpo,
        faixa(ehErroDeRede(error) ? 'Sem sinal agora. Quando a internet voltar, tente de novo.' : mensagemDeErro(error), 'alerta', 'Não deu para abrir'),
        tentarDeNovo());
      return;
    }
    if (acerto.situacao === 'invalido') {
      trocar(corpo, faixa('Este link não vale mais: o acerto pode ter sido desfeito para correção. Peça um novo a quem mandou.', 'perigo', 'Link inválido'));
      return;
    }
    if (acerto.situacao === 'vencido') {
      trocar(corpo, faixa('O link vale 30 dias e este já passou. Peça um novo a quem mandou.', 'alerta', 'Link vencido'));
      return;
    }
    if (acerto.situacao === 'assinado') {
      trocar(corpo, faixa(`Este acerto já foi assinado em ${quando(acerto.assinado_em)}. Obrigado!`, 'ok', 'Já assinado'));
      return;
    }
    mostrarAcerto(acerto);
  }

  function mostrarAcerto(acerto) {
    const quadro = quadroDeAssinatura();
    const erro = h('div', { role: 'alert' });
    const botao = h('button', { class: 'botao cheio', type: 'button', disabled: true }, 'Confirmar assinatura');
    quadro.aoMudar(() => {
      botao.disabled = !assinaturaSuficiente(quadro.tracos());
    });
    botao.addEventListener('click', async () => {
      const tracos = quadro.tracos();
      if (!assinaturaSuficiente(tracos)) return;
      botao.disabled = true;
      botao.textContent = 'Enviando…';
      trocar(erro);
      const { data: r, error } = await supabase.rpc('assinar_acerto', { p_token: codigo, p_assinatura: caminhoDosTracos(tracos) });
      if (error) {
        trocar(erro, faixa(ehErroDeRede(error) ? 'Sem sinal agora. A assinatura continua no quadro: tente de novo quando a internet voltar.' : mensagemDeErro(error), 'perigo'));
        botao.disabled = false;
        botao.textContent = 'Confirmar assinatura';
        return;
      }
      quadro.desligar();
      trocar(corpo,
        faixa(`Assinatura registrada em ${quando(r?.assinado_em)}. Obrigado!`, 'ok', 'Pronto'),
        h('p', { class: 'pequeno' }, 'Pode fechar esta página.'));
    });

    const saldo = Number(acerto.saldo);
    const resultado = saldo < 0 ? `A empresa paga ${dinheiro(-saldo)} para você.`
      : saldo > 0 ? `Você devolve ${dinheiro(saldo)} à empresa.` : 'Acerto zerado: ninguém deve nada.';
    const periodo = acerto.periodo_de && acerto.periodo_de !== acerto.data ? `${data(acerto.periodo_de)} a ${data(acerto.data)}` : data(acerto.data);
    const grupos = ['credito', 'vale', 'despesa', 'desconto']
      .map((tipo) => [tipo, (acerto.lancamentos ?? []).filter((l) => l.tipo === tipo)])
      .filter(([, lista]) => lista.length > 0);

    trocar(corpo,
      h('h1', null, `Acerto nº ${acerto.numero}/${acerto.ano}`),
      h('p', null, `${acerto.empresa} fechou o acerto de ${acerto.colaborador ?? 'você'} (${periodo}):`),
      h('div', { class: 'cartao destaque' }, h('div', { class: 'cartao-corpo' },
        h('span', { class: 'valor-grande' }, dinheiro(Math.abs(saldo))),
        h('span', { class: 'cartao-linha' }, resultado))),
      (acerto.viagens ?? []).length > 0 && h('p', { class: 'pequeno' },
        `Viagens: ${(acerto.viagens ?? []).map((v) => (v.parcial
          ? `de ${data(v.data_saida)}, cargas até ${data(acerto.data)} (${formatarPlaca(v.placa)})`
          : `${data(v.data_saida)} a ${data(v.data_chegada)} (${formatarPlaca(v.placa)})`)).join('; ')}.`),
      grupos.map(([tipo, lista]) => h('div', { class: 'grupo-valores' },
        h('h3', null, NOMES_TIPO[tipo]),
        lista.map((l) => h('div', { class: 'linha-valor' }, h('span', null, `${data(l.data)} · ${textoDoLancamento(l)}`), h('span', null, dinheiro(l.valor)))))),
      h('dl', { class: 'linha-dados resumo' },
        Number(acerto.saldo_anterior) !== 0 && [h('dt', null, 'Saldo do acerto anterior'), h('dd', null, dinheiro(acerto.saldo_anterior))],
        h('dt', null, '+ Vales'), h('dd', null, dinheiro(acerto.total_vales)),
        h('dt', null, '− Despesas pagas por você'), h('dd', null, dinheiro(acerto.total_despesas)),
        h('dt', null, '− Créditos'), h('dd', null, dinheiro(acerto.total_creditos)),
        h('dt', null, '+ Descontos'), h('dd', null, dinheiro(acerto.total_descontos)),
        h('dt', { class: 'total' }, '= Saldo'), h('dd', { class: 'total' }, dinheiro(acerto.saldo))),
      h('p', null, h('strong', null, 'Assine com o dedo no quadro abaixo.'), ' Ao assinar, você confirma que conferiu e concorda com os valores.'),
      quadro.elemento,
      erro,
      h('div', { class: 'acoes' },
        botao,
        h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => quadro.limpar() }, 'Limpar e assinar de novo')));
  }

  carregar().catch((e) => {
    console.error(e);
    trocar(corpo, faixa(mensagemDeErro(e), 'perigo'), tentarDeNovo());
  });

  return h('div', { class: 'pagina sem-abas' },
    h('header', { class: 'topo' }, h('div', { class: 'topo-linha' }, h('h1', null, 'HSA Frota'))),
    h('main', { class: 'conteudo assinar', id: 'conteudo', tabindex: '-1' },
      h('div', { class: 'marca-pequena' }, icone('caminhao', { tamanho: 20 }), ' Assinatura de acerto'),
      corpo));
}
